<template>
    <div class="carteras-list-container">
      <div v-if="loading" class="loading-indicator-gcn">Cargando carteras...</div>
      <div v-if="error" class="error-message-gcn">{{ error }}</div>
  
      <div v-if="!loading && !error">
        <div class="portfolio-actions-gcn">
          <button 
            class="btnGcn custom-btnGcn" 
            :class="{ 'disabled': isUserBronze }"
            @click="openCreateModal" 
            :disabled="isUserBronze"
            :title="isUserBronze ? 'Esta función no está disponible para usuarios Bronze' : 'Crear nueva cartera'"
          >
            <span class="material-icons-outlined">add</span> Crear Nueva Cartera
          </button>
          
          <!-- Sorting Controls -->
          <div class="sort-controls-gcn">
            <label class="sort-label-gcn">Ordenar por:</label>
            <select v-model="sortField" @change="updateSort" class="sort-select-gcn">
              <option value="name">Nombre</option>
              <option value="risk">Riesgo</option>
              <option value="initial_capital">Capital Inicial</option>
              <option value="real_capital">Capital Real</option>
              <option value="data">Fecha de Creación</option>
            </select>
            <button 
              @click="toggleSortDirection" 
              class="btnGcn btnGcn-sm sort-direction-btn-gcn"
              :title="sortDirection === 'asc' ? 'Cambiar a descendente' : 'Cambiar a ascendente'"
            >
              <span class="material-icons-outlined">
                {{ sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
              </span>
            </button>
          </div>
        </div>
  
        <ul v-if="sortedPortfolios.length > 0" class="portfolio-list-gcn">
          <li
            v-for="portfolio in sortedPortfolios"
            :key="portfolio.idc"
            class="portfolio-item-gcn"
            :class="{ selected: selectedPortfolioId === portfolio.idc }"
            @click="selectPortfolio(portfolio.idc)"
          >
            <div class="portfolio-item-gcn-details">
              <h3>
                {{ portfolio.name }}
                <span v-if="portfolio.alert_subscription !== undefined"
                      :class="['alert-status-gcn', portfolio.alert_subscription ? 'alert-status-gcn-subscribed' : 'alert-status-gcn-unsubscribed']">
                  {{ portfolio.alert_subscription ? 'Alertas ON' : 'Alertas OFF' }}
                </span>
              </h3>
              <div class="portfolio-item-gcn-meta">
                <span><strong>Capital Inicial:</strong> {{ formatCurrencyAnglo(portfolio.initial_capital, portfolio.currency) }}</span>
                <span><strong>Capital Real:</strong> {{ formatCurrencyAnglo(portfolio.real_capital, portfolio.currency) }}</span>
                <span><strong>Moneda:</strong> {{ portfolio.currency }}</span>
                <span><strong>Riesgo:</strong> {{ formatPercentage(portfolio.risk) }}</span>
                <span><strong>Estrategia:</strong> {{ portfolio.risk_strategy === 'support_price' ? 'Precio De Soporte' : 'Precio De Stop' }}</span>
                <span><strong>Creada:</strong> {{ formatDate(portfolio.data) }}</span>
               </div>
            </div>
            <div class="portfolio-item-gcn-actions">
              <button class="btnGcn btnGcn-sm custom-btnGcn" @click.stop="openUpdateModal(portfolio)" title="Editar">
                <span class="material-icons-outlined">edit</span>
              </button>
              <button
                  class="btnGcn btnGcn-sm custom-btnGcn"
                  :class="portfolio.alert_subscription ? 'active' : ''"
                  @click.stop="toggleAlertSubscription(portfolio)"
                  :title="portfolio.alert_subscription ? 'Desactivar Alertas' : 'Activar Alertas'">
                  <span class="material-icons-outlined">{{ portfolio.alert_subscription ? 'notifications_off' : 'notifications' }}</span>
              </button>
              <button class="btnGcn btnGcn-sm custom-btnGcn" @click.stop="clonePortfolio(portfolio)" title="Clonar">
                <span class="material-icons-outlined">content_copy</span>
              </button>
              <button class="btnGcn btnGcn-sm custom-btnGcn" @click.stop="exportPortfolioCsv(portfolio)" title="Exportar CSV" :disabled="isExporting && exportingPortfolioId === portfolio.idc">
                <span class="material-icons-outlined">download</span>
              </button>
              <button 
                v-if="!isLastPortfolio"
                class="btnGcn btnGcn-sm custom-btnGcn danger" 
                @click.stop="openDeleteModal(portfolio)" 
                title="Eliminar">
                <span class="material-icons-outlined">delete</span>
              </button>
              <button 
                v-if="isLastPortfolio"
                class="btnGcn btnGcn-sm custom-btnGcn" 
                @click.stop="openResetModal(portfolio)" 
                title="Reiniciar Cartera">
                <span class="material-icons-outlined">refresh</span>
              </button>
            </div>
          </li>
        </ul>
        <div v-else-if="portfolios.length === 0">
          <p>No se encontraron carteras. ¡Crea una para empezar!</p>
        </div>
      </div>
  
      <!-- Modals -->
      <Modal :show="showCreateModal" title="Crear Nueva Cartera" @close="closeCreateModal">
          <template #body>
              <PortfolioForm ref="createForm" mode="create" :initial-data="newPortfolioData" @submit="handleCreatePortfolio" :error="formError"/>
          </template>
          <template #footer>
              <button class="btnGcn custom-btnGcn-outline" @click="closeCreateModal">Cancelar</button>
              <button class="btnGcn custom-btnGcn" @click="$refs.createForm.submitForm()" :disabled="isSubmitting">
                  {{ isSubmitting ? 'Creando...' : 'Crear Cartera' }}
              </button>
          </template>
      </Modal>
  
      <Modal :show="showUpdateModal" title="Actualizar Cartera" @close="closeUpdateModal">
          <template #body>
               <PortfolioForm ref="updateForm" mode="update" :initial-data="portfolioToEdit" @submit="handleUpdatePortfolio" :error="formError"/>
          </template>
           <template #footer>
              <button class="btnGcn custom-btnGcn-outline" @click="closeUpdateModal">Cancelar</button>
              <button class="btnGcn custom-btnGcn" @click="$refs.updateForm.submitForm()" :disabled="isSubmitting">
                   {{ isSubmitting ? 'Actualizando...' : 'Actualizar Cartera' }}
              </button>
          </template>
      </Modal>
  
      <Modal :show="showDeleteModal" title="Confirmar Eliminación" @close="closeDeleteModal" size="sm">
          <template #body>
              <!-- *** FIXED: Replaced optional chaining *** -->
              <p>¿Estás seguro de que quieres eliminar la cartera <strong>{{ portfolioToDelete ? portfolioToDelete.name : '' }}</strong>?</p>
              <p>Esta acción no se puede deshacer.</p>
               <div v-if="formError" class="error-message-gcn" style="text-align: left;">{{ formError }}</div>
          </template>
           <template #footer>
              <button class="btnGcn custom-btnGcn-outline" @click="closeDeleteModal">Cancelar</button>
              <button class="btnGcn custom-btnGcn danger" @click="handleDeletePortfolio" :disabled="isSubmitting">
                   {{ isSubmitting ? 'Eliminando...' : 'Eliminar' }}
              </button>
          </template>
      </Modal>

      <Modal :show="showResetModal" title="Reiniciar Cartera" @close="closeResetModal">
          <template #body>
              <p>Vas a reiniciar la cartera <strong>{{ portfolioToReset ? portfolioToReset.name : '' }}</strong>.</p>
              <p>Esta acción eliminará todas las posiciones (abiertas, cerradas y en espera) y balances, manteniendo solo la configuración inicial.</p>
              <PortfolioForm ref="resetForm" mode="reset" :initial-data="portfolioToReset" @submit="handleResetPortfolio" :error="formError"/>
          </template>
          <template #footer>
              <button class="btnGcn custom-btnGcn-outline" @click="closeResetModal">Cancelar</button>
              <button class="btnGcn custom-btnGcn danger" @click="$refs.resetForm.submitForm()" :disabled="isSubmitting">
                  {{ isSubmitting ? 'Reiniciando...' : 'Reiniciar Cartera' }}
              </button>
          </template>
      </Modal>
  
      <!-- Generic Confirmation Modal -->
      <Modal :show="showConfirmModal" :title="confirmModalTitle" @close="closeConfirmModal" size="sm">
          <template #body>
              <p>{{ confirmModalMessage }}</p>
              <div v-if="formError && confirmModalAction" class="error-message-gcn" style="text-align: left;">{{ formError }}</div>
          </template>
          <template #footer>
              <button class="btnGcn custom-btnGcn-outline" @click="closeConfirmModal" :disabled="isConfirmSubmitting">Cancelar</button>
              <button class="btnGcn custom-btnGcn" @click="executeConfirmAction" :disabled="isConfirmSubmitting">
                  {{ isConfirmSubmitting ? 'Confirmando...' : 'Confirmar' }}
              </button>
          </template>
      </Modal>
  
    </div>
  </template>
  
  <script>
  // Make sure the paths are correct based on your project structure
  import apiService, { formatDateDMY, parseDate, formatCurrency } from './apiService'; // Assuming apiService is in src/services
  import Modal from './Modal.vue'; // Assuming Modal is in src/components
  import PortfolioForm from './PortfolioForm.vue'; // Assuming PortfolioForm is in the same directory
  import { mapGetters } from 'vuex';
  
  export default {
    name: 'CarterasList',
    components: {
      Modal,
      PortfolioForm,
    },
    props: {
        // Accept the selected portfolio ID from parent
        selectedPortfolioId: {
            type: String,
            default: null
        }
    },
    data() {
      return {
        portfolios: [],
        loading: false,
        error: null,
        formError: null, // Error specific to modal forms
        isSubmitting: false,
  
        // Modal states
        showCreateModal: false,
        showUpdateModal: false,
        showDeleteModal: false,
        showResetModal: false,
  
        // Data for modals
        newPortfolioData: { // Default structure for creation
          name: '',
          capital: 0,
          currency: 'EUR', // Default currency
          date: formatDateDMY(new Date()), // Default to today
          risk_percentage: 2.0 // Default risk
        },
        portfolioToEdit: null,
        portfolioToDelete: null,
        portfolioToReset: null,

        // Generic Confirmation Modal State
        showConfirmModal: false,
        confirmModalTitle: '',
        confirmModalMessage: '',
        confirmModalAction: null, // Stores the function to call on confirm
        confirmModalData: null, // Stores data needed by the confirmAction
        isConfirmSubmitting: false, // Specific submitting state for confirm modal

        // Sorting state
        sortField: 'name',
        sortDirection: 'asc',
        sortedPortfolios: [],
        exportError: null,
        isExporting: false,
        exportingPortfolioId: null,
      };
    },
    computed: {
      ...mapGetters(['_g_UserFeatures']),
      isLastPortfolio() {
        return this.portfolios.length === 1;
      },
      isUserBronze() {
        return this._g_UserFeatures?.is_bronze_user || false;
      }
    },
    created() {
      this.fetchPortfolios();
    },
    watch: {
      // No need to do anything when selectedPortfolioId changes
      // The UI will update automatically through props
    },
    methods: {
      formatCurrency, // Make helper available in template
      formatDate(dateString) {
        const date = parseDate(dateString);
        // Use Spanish locale for date formatting 'es-ES'
        return date ? date.toLocaleDateString('es-ES') : dateString;
      },
      async fetchPortfolios() {
        this.loading = true;
        this.error = null;
        try {
          const newPortfolios = await apiService.listPortfolios();
          
          // Check if the selected portfolio still exists in the new list
          if (this.selectedPortfolioId && !newPortfolios.some(p => p.idc === this.selectedPortfolioId)) {
            // Notify parent that the selected portfolio is no longer available
            this.$emit('portfolio-selected', null);
          }
          
          // Update the portfolios list without triggering unnecessary re-renders
          this.portfolios = newPortfolios;
          this.updateSort();
        } catch (err) {
          this.error = `Error al cargar las carteras: ${err.message}`;
        } finally {
          this.loading = false;
        }
      },
  
      selectPortfolio(portfolioId) {
        // Prevent deselecting by clicking the active portfolio
        if (this.selectedPortfolioId === portfolioId) {
          return;
        }

        this.$emit('portfolio-selected', portfolioId); // Emit new ID to select
      },
  
      // --- Modal Open/Close Handlers ---
      openCreateModal() {
        // Prevent opening modal if user is Bronze
        if (this.isUserBronze) {
          return;
        }
        
        this.formError = null;
        this.newPortfolioData = { // Reset to defaults
          name: '', capital: 0, currency: 'EUR',
          date: formatDateDMY(new Date()), risk_percentage: 2.0
        };
        this.showCreateModal = true;
      },
      closeCreateModal() { this.showCreateModal = false; },
  
      openUpdateModal(portfolio) {
         this.formError = null;
         this.portfolioToEdit = { ...portfolio }; // Clone to avoid modifying original object directly
         this.showUpdateModal = true;
      },
      closeUpdateModal() { this.showUpdateModal = false; this.portfolioToEdit = null; },
  
      openDeleteModal(portfolio) {
          this.formError = null;
          this.portfolioToDelete = portfolio;
          this.showDeleteModal = true;
      },
      closeDeleteModal() { this.showDeleteModal = false; this.portfolioToDelete = null; },

      openResetModal(portfolio) {
          this.formError = null;
          this.portfolioToReset = { 
            ...portfolio,
            capital: portfolio.initial_capital,
            risk_percentage: portfolio.risk
          };
          this.showResetModal = true;
      },
      closeResetModal() { this.showResetModal = false; this.portfolioToReset = null; },
  
      // --- Generic Confirmation Modal Methods ---
      openConfirmModal(title, message, action, data) {
        this.formError = null; // Clear previous form errors that might be displayed in this modal
        this.confirmModalTitle = title;
        this.confirmModalMessage = message;
        this.confirmModalAction = action; // This will be the function to call
        this.confirmModalData = data;     // Data to pass to the action function
        this.showConfirmModal = true;
      },
      closeConfirmModal() {
        this.showConfirmModal = false;
        this.confirmModalTitle = '';
        this.confirmModalMessage = '';
        this.confirmModalAction = null;
        this.confirmModalData = null;
        this.isConfirmSubmitting = false; // Reset submitting state
      },
      async executeConfirmAction() {
        if (this.confirmModalAction) {
          this.formError = null;
          this.isConfirmSubmitting = true;
          try {
            await this.confirmModalAction(this.confirmModalData); // Call the stored action
            // Success: The action itself should handle UI updates (toast, list refresh)
            this.closeConfirmModal();
          } catch (err) {
            this.formError = `Error: ${err.message || 'Ocurrió un error al confirmar la acción.'}`;
            console.error("Confirmation Action failed:", err);
            // Keep the modal open to show the error
          } finally {
            this.isConfirmSubmitting = false;
          }
        }
      },
  
      // --- Action Handlers ---
      async handleApiAction(actionPromise, successMessage, modalCloseMethod) {
          this.formError = null;
          this.isSubmitting = true;
          try {
              const result = await actionPromise;
              // Check for specific API error status if applicable
              if (result && result.status === 'error') {
                   throw new Error(result.message || 'Ocurrió un error desconocido.');
              }
              console.log(successMessage, result);
              
              // Refresh the list without forcing a re-render of the CarterasList component
              await this.fetchPortfolios();
  
              // Special handling for delete: deselect if the deleted one was selected
              if (modalCloseMethod === this.closeDeleteModal && this.selectedPortfolioId === this.portfolioToDelete?.idc) {
                  this.$emit('portfolio-selected', null);
              }
  
              if (modalCloseMethod) modalCloseMethod();
          } catch (err) {
              this.formError = `Error: ${err.message}`;
              console.error("API Action failed:", err);
          } finally {
              this.isSubmitting = false;
          }
      },
  
      handleCreatePortfolio(formData) {
         this.handleApiAction(
             apiService.createPortfolio(formData),
             'Portfolio created successfully',
             this.closeCreateModal
         );
      },
  
      handleUpdatePortfolio(formData) {
          const portfolioId = this.portfolioToEdit.idc;
          this.handleApiAction(
              apiService.updatePortfolio(portfolioId, formData),
              'Portfolio updated successfully',
              this.closeUpdateModal
          );
      },
  
      handleDeletePortfolio() {
          const portfolioId = this.portfolioToDelete.idc;
          this.handleApiAction(
              apiService.deletePortfolio(portfolioId),
              'Portfolio deleted successfully',
              this.closeDeleteModal // Pass the correct close method
          );
      },

      handleResetPortfolio(formData) {
          const portfolioId = this.portfolioToReset.idc;
          this.handleApiAction(
              apiService.resetPortfolio(portfolioId, formData),
              'Portfolio reset successfully',
              this.closeResetModal
          );
      },
  
      async performClonePortfolio(portfolio) {
        // Logic for actual cloning after confirmation
        try {
            await apiService.clonePortfolio(portfolio.idc);
            await this.fetchPortfolios(); // Refresh list
        } catch (err) {
            console.error(`Error al clonar la cartera "${portfolio.name}":`, err);
            throw new Error(`Al clonar la cartera: ${err.message}`);
        }
      },

      clonePortfolio(portfolio) {
        const title = 'Clonar Cartera';
        const message = `¿Confirmas que quieres clonar la cartera "${portfolio.name}"? Se creará una copia exacta.`;
        this.openConfirmModal(title, message, this.performClonePortfolio, portfolio);
      },
  
      async performToggleAlertSubscription(portfolio) {
        // This method contains the actual logic for toggling alerts
        const action = portfolio.alert_subscription ? 'unsubscribe' : 'subscribe';
        try {
            if (action === 'subscribe') {
                await apiService.subscribeToAlerts(portfolio.idc);
            } else {
                await apiService.unsubscribeFromAlerts(portfolio.idc);
            }
            // Always refresh the portfolios list to ensure updated alert status is displayed
            await this.fetchPortfolios();
        } catch (err) {
            console.error(`Error al ${action === 'subscribe' ? 'activar' : 'desactivar'} las alertas:`, err);
            throw new Error(`Al ${action === 'subscribe' ? 'activar' : 'desactivar'} las alertas: ${err.message}`);
        }
      },
  
      toggleAlertSubscription(portfolio) {
        const action = portfolio.alert_subscription ? 'unsubscribe' : 'subscribe';
        const title = action === 'subscribe' ? 'Activar Alertas' : 'Desactivar Alertas';
        const message = action === 'subscribe'
            ? `¿Confirmas que quieres activar las alertas por email para la cartera "${portfolio.name}"?`
            : `¿Confirmas que quieres desactivar las alertas por email para la cartera "${portfolio.name}"?`;
        
        this.openConfirmModal(title, message, this.performToggleAlertSubscription, portfolio);
      },
      formatCurrencyAnglo(value, currency = 'EUR') {
        const num = typeof value === 'number' ? value : Number(value);
        if (isNaN(num)) return '-';
        const isNegative = num < 0;
        const formattedNumber = this.formatNumberAnglo(Math.abs(num));
        const currencySymbol = currency === 'EUR' ? '€' : currency;
        return isNegative ? `-${formattedNumber}${currencySymbol}` : `${formattedNumber}${currencySymbol}`;
      },
      formatNumberAnglo(value, decimals = 2) {
        const num = typeof value === 'number' ? value : Number(value);
        if (isNaN(num)) return '-';
        const absValue = Math.abs(num);
        const parts = absValue.toFixed(decimals).split('.');
        const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        const decimalPart = parts[1];
        return decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
      },
      formatPercentage(value) {
        const num = typeof value === 'number' ? value : Number(value);
        if (isNaN(num)) return 'N/A';
        return `${this.formatNumberAnglo(num)}%`;
      },
      formatPrice(value, decimals = 2) {
        const num = typeof value === 'number' ? value : Number(value);
        if (isNaN(num)) return 'N/A';
        const numDecimals = (typeof decimals === 'number' && decimals >= 0) ? decimals : 2;
        return this.formatNumberAnglo(num, numDecimals);
      },
      formatDistanceToEntry(pos) {
        if (typeof pos.entry_distance === 'number') return this.formatPercentage(pos.entry_distance);
        if (typeof pos.current_price === 'number' && typeof pos.in_price === 'number' && pos.current_price !== 0) {
          const distance = (Math.abs(pos.in_price - pos.current_price) / pos.current_price) * 100;
          return this.formatPercentage(distance);
        }
        if (typeof pos.distance_to_entry === 'number') return this.formatPercentage(pos.distance_to_entry);
        return 'N/A';
      },
      updateSort() {
        this.sortedPortfolios = this.portfolios.slice().sort((a, b) => {
          const aValue = a[this.sortField];
          const bValue = b[this.sortField];
          
          // Handle date sorting specially
          if (this.sortField === 'data') {
            const aDate = parseDate(aValue);
            const bDate = parseDate(bValue);
            if (aDate && bDate) {
              return this.sortDirection === 'asc' ? aDate - bDate : bDate - aDate;
            }
            return 0;
          }
          
          // Handle string sorting
          if (typeof aValue === 'string' && typeof bValue === 'string') {
            return this.sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
          } 
          // Handle number sorting
          else if (typeof aValue === 'number' && typeof bValue === 'number') {
            return this.sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
          }
          
          return 0;
        });
      },
      toggleSortDirection() {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        this.updateSort();
      },
      async exportPortfolioCsv(portfolio) {
        this.exportError = null;
        this.isExporting = true;
        this.exportingPortfolioId = portfolio.idc;
        try {
          // Fetch data from various endpoints
          const [waiting, open, closed, balances] = await Promise.all([
            apiService.listWaitingPositions(portfolio.idc).catch(() => []),
            apiService.listOpenPositions(portfolio.idc).catch(() => []),
            apiService.listClosedPositions(portfolio.idc).catch(() => []),
            apiService.listDailyBalances(portfolio.idc).catch(() => []),
          ]);

          const formatDateES = (d) => {
            return d ? this.formatDate(d) : '-';
          };

          const DELIM = ';';
          const csvEscape = (val) => {
            if (val === null || val === undefined) return '';
            const str = String(val).replace(/\r?\n|\r/g, ' ').replace(/"/g, '""');
            return `"${str}"`;
          };

          // Build Waiting Positions CSV rows
          const waitingRows = waiting.map(pos => ({
            'Ticker': pos.ticker,
            'Acciones': this.formatNumberAnglo(pos.instrument_number, 0),
            'Riesgo': this.formatPercentage(pos.risk),
            'P. Entrada': this.formatPrice(pos.in_price, pos.stock_decimals),
            'P. Salida': this.formatPrice(pos.out_price, pos.stock_decimals),
            'P. Soporte': this.formatPrice(pos.support_price, pos.stock_decimals),
            'F. Soporte': formatDateES(pos.support_date),
            'Valor Divisa': pos.currency_value ? this.formatNumberAnglo(pos.currency_value, 4) : 'N/A',
            'Dist. Entrada': this.formatDistanceToEntry(pos),
            'Notas': pos.notes || ''
          }));

          // Build Open Positions CSV rows
          const openRows = open.map(pos => ({
            'Ticker': pos.ticker,
            'Riesgo': this.formatPercentage(pos.risk),
            'Acciones': this.formatNumberAnglo(pos.instrument_number, 0),
            'P. Entrada': this.formatPrice(pos.in_price, pos.stock_decimals),
            'P. Salida': this.formatPrice(pos.out_price, pos.stock_decimals),
            'P. Soporte': this.formatPrice(pos.support_price, pos.stock_decimals),
            'F. Soporte': formatDateES(pos.support_date),
            'Valor Divisa': this.formatPrice(pos.in_currency, 2),
            'Montante': this.formatCurrencyAnglo(pos.amount, portfolio.currency || 'EUR'),
            'Notas': pos.notes || ''
          }));

          // Build Closed Positions CSV rows
          const closedRows = closed.map(pos => ({
            'Ticker': pos.ticker,
            'Acciones': this.formatNumberAnglo(pos.instrument_number, 0),
            'P. Entrada': this.formatPrice(pos.in_price, pos.stock_decimals),
            'Com. Ap.': this.formatCurrencyAnglo(pos.open_fee, portfolio.currency || 'EUR'),
            'Div. Entrada': this.formatPrice(pos.in_currency, 2),
            'P. Salida': this.formatPrice(pos.user_out_price || pos.out_price, pos.stock_decimals),
            'Com. Ci.': this.formatCurrencyAnglo(pos.close_fee, portfolio.currency || 'EUR'),
            'Div. Salida': this.formatPrice(pos.out_currency, 2),
            'F. Entrada': formatDateES(pos.in_formatted_date || pos.in_date),
            'F. Salida': formatDateES(pos.out_formatted_date || pos.out_date),
            'F. Soporte': formatDateES(pos.support_date),
            'Nº Onda': pos.wave_number !== undefined ? pos.wave_number : '-',
            'Total': this.formatCurrencyAnglo(pos.total, portfolio.currency || 'EUR'),
            'Notas': pos.notes || ''
          }));

          // Build Balances CSV rows
          const balanceRows = balances.map(bal => ({
            'Fecha': formatDateES(bal.date),
            'Cash': this.formatCurrencyAnglo(bal.cash, portfolio.currency || 'EUR'),
            'Capital Ficticio': this.formatCurrencyAnglo(bal.fictitious_capital, portfolio.currency || 'EUR'),
            'Capital Real': this.formatCurrencyAnglo(bal.real_capital, portfolio.currency || 'EUR'),
            'Tipo': bal.transaction_type_name || bal.transaction_type,
            'Descripción': bal.description || '',
            'Variación': bal.variation_capital !== undefined ? this.formatCurrencyAnglo(bal.variation_capital, portfolio.currency || 'EUR') : '-'
          }));

          const buildCsvSection = (title, rows) => {
            if (!rows || rows.length === 0) return `${title}\nSin datos\n\n`;
            const headers = Object.keys(rows[0]);
            const headerLine = headers.map(h => csvEscape(h)).join(DELIM);
            const csvRows = rows.map(r => headers.map(h => csvEscape(r[h])).join(DELIM));
            return `${title}\n${headerLine}\n${csvRows.join('\n')}\n\n`;
          };

          const sections = [];
          sections.push(buildCsvSection('Valores en Espera', waitingRows));
          sections.push(buildCsvSection('Posiciones Abiertas', openRows));
          sections.push(buildCsvSection('Posiciones Cerradas', closedRows));
          sections.push(buildCsvSection('Balances Diarios', balanceRows));

          const csvContent = `Exportación de Cartera "${portfolio.name}" (${new Date().toLocaleString()})\n\n` + sections.join('');

          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          const safeName = portfolio.name.replace(/[^a-z0-9_\-]/gi, '_').toLowerCase();
          link.download = `portfolio_${safeName}_${Date.now()}.csv`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        } catch (err) {
          console.error('Error exportando CSV:', err);
          this.exportError = `Error exportando CSV: ${err.message}`;
          alert(this.exportError);
        } finally {
          this.isExporting = false;
          this.exportingPortfolioId = null;
        }
      },
    },
  };
  </script>
  
  <style scoped>
  .carteras-list-container {
    padding: 0;
    color: var(--color-text-primary);
    font-size: 0.95rem;
    animation: fadeIn 0.4s ease-out;
  }
  
  .carteras-list-container h2 {
    font-size: 1.75rem;
    margin-bottom: 2.5rem;
    color: var(--color-text-primary);
    font-weight: 700;
    padding: 0;
    border: none;
    position: relative;
    letter-spacing: -0.02em;
    line-height: 1.2;
  }
  
  .carteras-list-container h2::after {
    content: '';
    position: absolute;
    bottom: -12px;
    left: 0;
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, var(--color-highlight), rgba(var(--color-highlight-rgb), 0.3));
    border-radius: 2px;
  }
  
  .material-icons-outlined {
    font-size: 16px;
    line-height: 1;
    vertical-align: middle;
    margin-right: 4px;
  }
  
  .btnGcn-sm .material-icons-outlined {
    font-size: 16px;
    margin-right: 0;
  }
  
  .portfolio-actions-gcn .material-icons-outlined {
    margin-right: 8px;
  }
  
  .portfolio-actions-gcn {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding: 8px 12px;
    border-radius: 5px;
    background: linear-gradient(to right, var(--color-active-surface), var(--color-background));
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .sort-controls-gcn {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  
  .sort-label-gcn {
    font-size: 0.85rem;
    color: var(--color-text-secondary);
    font-weight: 500;
    white-space: nowrap;
  }
  
  .sort-select-gcn {
    padding: 6px 12px;
    border: 1px solid var(--color-outline);
    border-radius: 6px;
    background-color: var(--color-surface);
    color: var(--color-text-primary);
    font-size: 0.85rem;
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 140px;
  }
  
  .sort-select-gcn:hover {
    border-color: var(--color-text-secondary);
    background-color: var(--color-active-surface);
  }
  
  .sort-select-gcn:focus {
    outline: none;
    border-color: var(--color-highlight);
    box-shadow: 0 0 0 2px rgba(var(--color-highlight-rgb), 0.25);
  }
  
  .sort-direction-btn-gcn {
    background-color: var(--color-surface);
    border: 1px solid var(--color-outline);
    min-width: 36px;
    height: 36px;
  }
  
  .sort-direction-btn-gcn:hover {
    background-color: var(--color-active-surface);
    border-color: var(--color-text-secondary);
  }
  
  .btnGcn-sm {
    min-width: 36px;
    height: 36px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    transition: all 0.2s ease;
    border: none;
    background-color: var(--color-surface);
    color: var(--color-text-secondary);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
  
  .btnGcn-sm:hover {
    background-color: var(--color-active-surface);
    color: var(--color-text-primary);
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }
  
  .btnGcn-sm:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(var(--color-highlight-rgb), 0.25);
  }
  
  /* Portfolio list styling */
  .portfolio-list-gcn {
    list-style: none;
    padding: 0;
    margin: 0;
    display: block;
    gap: 0;
    background-color: transparent;
    border-radius: 0;
    overflow: visible;
    box-shadow: none;
  }
  
  .portfolio-item-gcn {
    border: 1px solid var(--color-outline);
    border-radius: 12px;
    margin-bottom: 16px;
    padding: 24px 28px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 20px;
    background-color: var(--color-surface);
    transition: all 0.2s ease;
    cursor: pointer;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    position: relative;
    animation: fadeIn 0.3s ease-out both;
    animation-delay: calc(var(--index, 0) * 30ms);
  }
  
  .portfolio-item-gcn:first-child {
    border-radius: 12px;
  }
  
  .portfolio-item-gcn:last-child {
    border-radius: 12px;
    margin-bottom: 0;
  }
  
  .portfolio-item-gcn:only-child {
    border-radius: 12px;
  }
  
  .portfolio-item-gcn:hover {
    background-color: var(--color-active-surface);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    border-color: var(--color-text-secondary);
  }
  
  .portfolio-item-gcn.selected {
    background-color: var(--color-active-surface);
    box-shadow: 0 0 0 2px var(--color-highlight);
    border-color: var(--color-highlight);
  }
  
  .portfolio-item-gcn-details {
    flex: 1;
    min-width: 300px;
  }
  
  .portfolio-item-gcn-details h3 {
    font-size: 1.25rem;
    margin-top: 0;
    margin-bottom: 16px;
    color: var(--color-text-primary);
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    font-weight: 600;
    letter-spacing: -0.01em;
    line-height: 1.3;
  }
  
  .portfolio-item-gcn-meta {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px 24px;
    color: var(--color-text-secondary);
    font-size: 0.9rem;
    line-height: 1.4;
  }
  
  .portfolio-item-gcn-meta span {
    position: relative;
    padding: 0;
    display: flex;
    align-items: center;
  }
  
  .portfolio-item-gcn-meta span strong {
    color: var(--color-text-primary);
    font-weight: 600;
    margin-right: 8px;
    min-width: fit-content;
  }
  
  .portfolio-item-gcn-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: flex-start;
    margin-top: 4px;
  }
  
  .alert-status-gcn {
    font-size: 0.75rem;
    padding: 4px 12px;
    border-radius: 16px;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    margin-left: 12px;
    transition: all 0.2s ease;
  }
  
  .alert-status-gcn-subscribed {
    background-color: rgba(34, 197, 94, 0.1);
    color: rgb(21, 128, 61);
    border: 1px solid rgba(34, 197, 94, 0.2);
  }
  
  .alert-status-gcn-unsubscribed {
    background-color: rgba(239, 68, 68, 0.1);
    color: rgb(185, 28, 28);
    border: 1px solid rgba(239, 68, 68, 0.2);
  }
  
  .alert-status-gcn-pending {
    background-color: rgba(245, 158, 11, 0.1);
    color: rgb(146, 64, 14);
    border: 1px solid rgba(245, 158, 11, 0.2);
  }
  
  /* Loading and error states */
  .loading-indicator-gcn {
    color: var(--color-text-secondary);
    text-align: center;
    padding: 20px;
    font-size: 0.9em;
    background-color: rgba(0, 0, 0, 0.03);
    border-radius: 8px;
    margin: 15px 0;
  }
  
  .error-message-gcn {
    color: var(--color-error);
    padding: 12px 16px;
    border-radius: 6px;
    margin-bottom: 16px;
    background-color: rgba(220, 53, 69, 0.08);
    font-size: 0.9em;
    border-left: 3px solid var(--color-error);
    box-shadow: 0 2px 8px rgba(220, 53, 69, 0.1);
  }
  
  /* Button styling to match Balance.vue */
  .custom-btnGcn {
    background-color: var(--color-highlight);
    color: var(--color-text-on-highlight);
    border: 1px solid var(--color-highlight);
    transition: all 0.2s ease;
    padding: 6px 12px;
    font-size: 0.85rem;
    display: inline-flex;
    align-items: center;
    cursor: pointer;
  }

  .custom-btnGcn:hover:not(.disabled) {
    background-color: var(--color-text-primary);
    color: var(--color-background);
    border-color: var(--color-text-primary);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px var(--color-outline);
  }

  .custom-btnGcn:active:not(.disabled) {
    transform: translateY(0);
    box-shadow: 0 1px 4px var(--color-outline);
  }

  .custom-btnGcn:focus:not(.disabled) {
    outline: none;
    box-shadow: 0 0 0 3px var(--color-active-surface);
  }

  .custom-btnGcn .material-icons-outlined {
    font-size: 1.2em;
  }

  /* Disabled button styles */
  .custom-btnGcn.disabled {
    background-color: var(--color-outline);
    color: var(--color-text-secondary);
    border-color: var(--color-outline);
    cursor: not-allowed;
    opacity: 0.6;
    transform: none;
    box-shadow: none;
  }

  .custom-btnGcn.disabled:hover {
    background-color: var(--color-outline);
    color: var(--color-text-secondary);
    border-color: var(--color-outline);
    transform: none;
    box-shadow: none;
  }
  
  /* Danger button variant */
  .custom-btnGcn.danger {
    background-color: var(--color-error);
    color: white;
    border-color: var(--color-error);
  }
  
  .custom-btnGcn.danger:hover {
    background-color: #dc3545;
    color: white;
    border-color: #dc3545;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
  }

  /* Outline button variant for modals */
  .custom-btnGcn-outline {
    background-color: var(--color-surface);
    color: var(--color-text-primary);
    border: 1px solid var(--color-outline);
    font-size: 0.85em;
    padding: 6px 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
  }

  .custom-btnGcn-outline:hover {
    background-color: var(--color-active-surface);
    border-color: var(--color-text-secondary);
  }

  .custom-btnGcn-outline:active {
    background-color: var(--color-outline);
  }

  .custom-btnGcn-outline:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-active-surface);
  }
  
  /* Small buttons for actions */
  .btnGcn-sm {
    min-width: 32px;
    height: 32px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    transition: all 0.15s ease;
    border: none;
    background-color: var(--color-background);
    color: var(--color-text-secondary);
    cursor: pointer;
  }
  
  .btnGcn-sm:hover {
    background-color: var(--color-surface);
    color: var(--color-text-primary);
  }
  
  .btnGcn-sm:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(var(--color-highlight-rgb), 0.25);
  }
  
  /* Alert status badges */
  .alert-status-gcn {
    font-size: 0.75rem;
    padding: 4px 10px;
    border-radius: 12px;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    margin-left: 12px;
    transition: all 0.15s ease;
    text-transform: uppercase;
    letter-spacing: 0.025em;
  }
  
  /* Media queries for responsiveness */
  @media (max-width: 768px) {
    .portfolio-actions-gcn {
      flex-direction: column;
      align-items: stretch;
      gap: 12px;
    }
    
    .sort-controls-gcn {
      justify-content: center;
      width: 100%;
    }
    
    .sort-select-gcn {
      flex: 1;
      min-width: 0;
    }
    
    .portfolio-item-gcn {
      padding: 20px;
      gap: 16px;
    }
    
    .portfolio-item-gcn-details {
      min-width: auto;
    }
    
    .portfolio-item-gcn-details h3 {
      font-size: 1.1rem;
      margin-bottom: 12px;
    }
    
    .portfolio-item-gcn-meta {
      grid-template-columns: 1fr;
      gap: 8px;
    }
    
    .portfolio-item-gcn-actions {
      margin-top: 0;
      width: 100%;
      justify-content: flex-end;
    }
    
    .carteras-list-container h2 {
      font-size: 1.5rem;
      margin-bottom: 2rem;
    }
  }
  
  /* Animation */
  @keyframes fadeIn {
    from { 
      opacity: 0; 
      transform: translateY(8px);
    }
    to { 
      opacity: 1; 
      transform: translateY(0);
    }
  }
  
  /* Empty state */
  .empty-state {
    text-align: center;
    padding: 4rem 2rem;
    color: var(--color-text-secondary);
  }
  
  .empty-state h3 {
    margin-bottom: 1rem;
    color: var(--color-text-primary);
    font-weight: 600;
    font-size: 1.25rem;
  }
  
  .empty-state p {
    margin-bottom: 2rem;
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.6;
  }
  </style>